import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Navigation, ChevronDown } from "lucide-react";
import Particles from "@/components/Particles";
import FiberStrands from "@/components/FiberStrands";

const HERO_BG = "/hero.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.15 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero({ settings, onParticipate }) {
  const details = [
    { icon: Calendar, label: "Data", value: settings?.event_date },
    { icon: Clock, label: "Orario", value: settings?.event_time },
    { icon: MapPin, label: "Location", value: settings?.location },
    { icon: Navigation, label: "Città", value: settings?.city },
  ];

  return (
    <section
      id="hero"
      className="relative flex min-h-screen w-full items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/30 to-transparent" />
      </div>
      <div className="absolute inset-0 opacity-70">
        <Particles density={70} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-28 md:px-10">
        <motion.span
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mb-6 inline-block rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-white/70 backdrop-blur"
        >
          Rea Company Srl · Fastweb + Vodafone
        </motion.span>

        <motion.h1
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="max-w-4xl font-display text-5xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
        >
          Connessi{" "}
          <span className="relative inline-block">
            <span className="fiber-text">dalla Sfida</span>
            <FiberStrands className="absolute -bottom-3 left-0 h-6 w-full sm:-bottom-4 sm:h-8" />
          </span>
        </motion.h1>

        <motion.p
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-6 max-w-2xl text-base text-white/70 md:text-lg"
        >
          {settings?.hero_subtitle}
        </motion.p>

        {/* Details */}
        <motion.div
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-10 grid max-w-3xl grid-cols-2 gap-3 md:grid-cols-4"
        >
          {details.map((d) => (
            <div key={d.label} className="glass rounded-xl px-4 py-3" data-testid={`hero-detail-${d.label.toLowerCase()}`}>
              <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
                <d.icon size={13} className="text-[#f5c542]" /> {d.label}
              </div>
              <div className="font-display text-sm font-bold text-white">{d.value}</div>
            </div>
          ))}
        </motion.div>

        <motion.div custom={4} variants={fadeUp} initial="hidden" animate="show" className="mt-10">
          <button
            onClick={onParticipate}
            className="btn-neon flex items-center gap-2 rounded-full px-8 py-4 font-display text-sm font-extrabold uppercase tracking-[0.12em]"
            data-testid="hero-cta"
          >
            Partecipa alla Convention
          </button>
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/40"
      >
        <ChevronDown size={26} />
      </motion.div>
    </section>
  );
}
